import React from 'react';
import { FaCheck } from 'react-icons/fa';
import './css/TheoryArticleBody.css';

export type TheoryTableRow = {
  term: string;
  description: string;
};

export type TheoryArticleSections = {
  subtitle?: string;
  definition?: {
    title: string;
    paragraphs: string[];
  };
  keyConstructions?: {
    title: string;
    rows: TheoryTableRow[];
  };
  operators?: {
    title: string;
    rows?: TheoryTableRow[];
    items?: string[];
  };
  miniExample?: {
    title: string;
    code?: React.ReactNode;
    caption?: string;
    resultTable?: {
      headers: string[];
      rows: string[][];
    };
  };
  exampleExplanation?: {
    title: string;
    paragraphs: string[];
  };
  commonMistakes?: {
    title: string;
    items: string[];
  };
  remember?: {
    title: string;
    items: string[];
  };
};

type TheoryArticleBodyProps = {
  sections: TheoryArticleSections;
};

function TheoryBlockCard({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`theory-block-card ${className}`.trim()}>
      <h2 className="theory-block-card__title">{title}</h2>
      <div className="theory-block-card__body">{children}</div>
    </section>
  );
}

function TheoryTermTable({ rows }: { rows: TheoryTableRow[] }) {
  return (
    <table className="theory-term-table">
      <tbody>
        {rows.map((row) => (
          <tr key={row.term}>
            <th scope="row">{row.term}</th>
            <td>{row.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function TheoryArticleBody({ sections }: TheoryArticleBodyProps) {
  const {
    definition,
    keyConstructions,
    operators,
    miniExample,
    exampleExplanation,
    commonMistakes,
    remember,
  } = sections;

  return (
    <div className="theory-article-body">
      {definition && (
        <TheoryBlockCard title={definition.title} className="theory-block-card--full">
          <div className="theory-prose">
            {definition.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </TheoryBlockCard>
      )}

      {(keyConstructions || operators) && (
        <div className="theory-block-grid theory-block-grid--2">
          {keyConstructions && (
            <TheoryBlockCard title={keyConstructions.title}>
              <TheoryTermTable rows={keyConstructions.rows} />
            </TheoryBlockCard>
          )}
          {operators && (
            <TheoryBlockCard title={operators.title}>
              {operators.rows && operators.rows.length > 0 ? (
                <TheoryTermTable rows={operators.rows} />
              ) : (
                <ul className="theory-bullet-list">
                  {(operators.items ?? []).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              )}
            </TheoryBlockCard>
          )}
        </div>
      )}

      {(miniExample || exampleExplanation) && (
        <div
          className={`theory-block-grid${
            miniExample && exampleExplanation ? ' theory-block-grid--2' : ''
          }`}
        >
          {miniExample && (
            <TheoryBlockCard
              title={miniExample.title}
              className={!exampleExplanation ? 'theory-block-card--full' : ''}
            >
              <div
                className={`theory-mini-example${
                  miniExample.code && miniExample.resultTable
                    ? ''
                    : ' theory-mini-example--single'
                }`}
              >
                {miniExample.code != null && (
                  <pre className="theory-code-block">
                    <code>{miniExample.code}</code>
                  </pre>
                )}
                {miniExample.resultTable && (
                  <div className="theory-result-wrap">
                    {miniExample.caption && (
                      <p className="theory-result-caption">{miniExample.caption}</p>
                    )}
                    <table className="theory-result-table">
                    <thead>
                      <tr>
                        {miniExample.resultTable.headers.map((header) => (
                          <th key={header} scope="col">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {miniExample.resultTable.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </TheoryBlockCard>
          )}
          {exampleExplanation && (
            <TheoryBlockCard title={exampleExplanation.title}>
              <div className="theory-prose">
                {exampleExplanation.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </TheoryBlockCard>
          )}
        </div>
      )}

      {(commonMistakes || remember) && (
        <div className="theory-block-grid theory-block-grid--2">
          {commonMistakes && (
            <TheoryBlockCard title={commonMistakes.title}>
              <ul className="theory-bullet-list">
                {commonMistakes.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </TheoryBlockCard>
          )}
          {remember && (
            <TheoryBlockCard title={remember.title}>
              <ul className="theory-check-list">
                {remember.items.map((item, index) => (
                  <li key={index}>
                    <span className="theory-check-list__icon" aria-hidden>
                      <FaCheck />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </TheoryBlockCard>
          )}
        </div>
      )}
    </div>
  );
}
